<template>
  <li :class="['ra-hotspots-poi', { 'ra-hotspots-poi--active': isActive }]" :style="rootStyles">
    <button
      ref="tooltipActivator"
      type="button"
      :aria-label="markerButtonAccessibleLabel"
      aria-haspopup="dialog"
      :data-poi-id="poi.id"
      class="ra-hotspots-poi__marker js-ra-hotspots-poi-marker"
      :data-refapp-tagging-target="`marker-${poi.id}`"
      @click="handleActivatorClick"
    >
      <div class="ra-hotspots-poi__marker-inner"></div>
    </button>

    <Transition name="ra-hotspots-tooltip-fade">
      <div
        v-if="isActive"
        ref="tooltip"
        role="dialog"
        class="ra-hotspots-poi__tooltip"
        aria-modal="true"
        tabindex="-1"
        :style="tooltipStyles"
      >
        <div ref="tooltipScrollTarget" class="ra-hotspots-poi__tooltip-scroll-target"></div>

        <button
          :aria-label="modalCloseButtonAccessibleLabel"
          class="ra-hotspots-poi__close"
          @click="closeTooltip"
        >
          <x-mark-icon />
        </button>

        <div v-if="poi.content" class="ra-hotspots-poi__rte" v-html="poi.content"></div>

        <a
          v-if="poi.cta?.url && poi.cta?.label"
          class="ra-hotspots-poi__cta"
          :href="poi.cta?.url"
          :target="poi.cta?.target || '_self'"
          :data-refapp-tagging-target="`modal-cta-${poi.id}`"
        >
          <span>{{ poi.cta.label }}</span>

          <span class="ra-hotspots-poi__cta-icon">
            <chevron-right-icon />
          </span>
        </a>
      </div>
    </Transition>
  </li>
</template>

<script>
import XMarkIcon from './icons/x-mark-icon.vue';
import ChevronRightIcon from './icons/chevron-right-icon.vue';
import {
  getElementDistancesWithinContainer,
  getElementViewportDistances,
} from '../../../shared/utils/dom/position';
import {
  SCREEN_SIZE,
  TOOLTIP_OFFSET_Y_TO_ANCHOR_PIXELS as TOOLTIP_OFFSET_TO_ANCHOR_PIXELS,
} from '../constants';
import { trapFocus } from '../../../shared/utils/dom/focus';
import { onClickOutside } from '../../../shared/utils/dom/events';
import { clamp } from '../../../shared/utils/math';

export default {
  name: 'refapp-hotspots-poi',
  components: {
    ChevronRightIcon,
    XMarkIcon,
  },
  inject: ['markerButtonAccessibleLabel', 'modalCloseButtonAccessibleLabel'],
  props: {
    poi: {
      type: Object,
      required: true,
    },
    currentPoiPositionKey: {
      type: String,
      required: true,
    },
    isActive: {
      type: Boolean,
      required: true,
    },
    containerSize: {
      type: Object,
      required: true,
    },
    containerRef: {
      type: HTMLDivElement,
      required: true,
    },
    imageNaturalSize: {
      type: Object,
      required: true,
    },
    screenSizeKey: {
      type: String,
      required: true,
    },
  },
  emits: ['openTooltip', 'closeTooltip'],
  data() {
    return {
      tooltipSize: {
        width: 0,
        height: 0,
      },
      markerSize: {
        width: 0,
        height: 0,
      },
      deactivateFocusTrap: null,
      cleanupClickOutside: null,
    };
  },
  computed: {
    rootStyles() {
      return {
        top: `${this.markerPositionPercents.top}%`,
        left: `${this.markerPositionPercents.left}%`,
      };
    },
    markerPositionPercents() {
      if (!this.imageNaturalSize.width) {
        return {
          top: 0,
          left: 0,
        };
      }

      const { x, y } = this.poi.position[this.currentPoiPositionKey];

      const topPercent = Number(y) * 100;
      const leftPercent = Number(x) * 100;

      const halfMarkerHeight = this.markerSize.height / 2;
      const halfMarkerWidth = this.markerSize.width / 2;

      const halfMarkerHeightPercents = (halfMarkerHeight / this.containerSize.height) * 100;
      const halfMarkerWidthPercents = (halfMarkerWidth / this.containerSize.width) * 100;

      return {
        top: clamp(topPercent, halfMarkerHeightPercents, 100 - halfMarkerHeightPercents),
        left: clamp(leftPercent, halfMarkerWidthPercents, 100 - halfMarkerWidthPercents),
      };
    },
    markerPositionPixels() {
      return {
        top: (this.containerSize.height * this.markerPositionPercents.top) / 100,
        left: (this.containerSize.width * this.markerPositionPercents.left) / 100,
      };
    },
    tooltipTransformPosition() {
      const transformPosition = {
        x: '-50%',
        y: `${TOOLTIP_OFFSET_TO_ANCHOR_PIXELS}px`,
      };

      if (!this.tooltipSize.width && !this.tooltipSize.height) {
        return transformPosition;
      }

      const { optimalHorizontal, optimalVertical } = this.calculateOptimalPlacement();

      const { width: activatorWidth, height: activatorHeight } =
        this.$refs.tooltipActivator.getBoundingClientRect();

      if (optimalVertical === 'top') {
        transformPosition.y = `calc(-100% - ${activatorHeight + TOOLTIP_OFFSET_TO_ANCHOR_PIXELS}px)`;
      }

      if (this.screenSizeKey === SCREEN_SIZE.sm) {
        const tooltipLeftOffsetRelativeToContainer =
          (this.containerSize.width - this.tooltipSize.width + this.markerSize.width) / 2;

        const tooltipLeftOffsetRelativeToMarker =
          this.markerPositionPixels.left - tooltipLeftOffsetRelativeToContainer;

        transformPosition.x = `${-tooltipLeftOffsetRelativeToMarker}px`;
      } else if (optimalHorizontal === 'right') {
        transformPosition.x = `${TOOLTIP_OFFSET_TO_ANCHOR_PIXELS}px`;
      } else if (optimalHorizontal === 'left') {
        transformPosition.x = `calc(-100% - ${activatorWidth + TOOLTIP_OFFSET_TO_ANCHOR_PIXELS}px)`;
      }

      return transformPosition;
    },
    tooltipStyles() {
      return {
        '--hotspot-tooltip-transform-x': this.tooltipTransformPosition.x,
        '--hotspot-tooltip-transform-y': this.tooltipTransformPosition.y,
      };
    },
  },
  watch: {
    async isActive(newVal) {
      if (!newVal) {
        this.deactivateFocusTrap?.();
        this.deactivateFocusTrap = null;
        this.cleanupClickOutside?.();
        this.cleanupClickOutside = null;
        this.tooltipSize = {
          width: 0,
          height: 0,
        };
        return;
      }

      await this.$nextTick();

      const { width, height } = this.$refs.tooltip.getBoundingClientRect();
      this.tooltipSize = {
        width,
        height,
      };

      this.deactivateFocusTrap = trapFocus(this.$refs.tooltip, {
        clickOutsideDeactivates: true,
        returnFocus: false,
        initialFocus: false,
      });
      setTimeout(() => {
        this.cleanupClickOutside = onClickOutside(this.$refs.tooltip, () => {
          this.closeTooltip({ returnFocusToMarker: false });
        });
        this.$refs.tooltip.focus({ preventScroll: true });

        // if tooltip is not completely visible in viewport - scroll to the top of tooltip
        const { top, bottom } = getElementViewportDistances(this.$refs.tooltip);
        if (top < 0) {
          this.$refs.tooltipScrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (bottom < 0) {
          this.$refs.tooltipActivator.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
  },
  mounted() {
    this.setMarkerSize();
  },
  methods: {
    handleActivatorClick() {
      if (!this.isActive) {
        this.$emit('openTooltip');
      }
    },
    setMarkerSize() {
      if (this.$refs.tooltipActivator) {
        const { width, height } = this.$refs.tooltipActivator.getBoundingClientRect();
        this.markerSize = {
          width,
          height,
        };
      }
    },
    closeTooltip({ returnFocusToMarker = true } = {}) {
      this.$emit('closeTooltip', { returnFocusToMarker });
    },
    calculateOptimalPlacement() {
      const distances = getElementDistancesWithinContainer(
        this.$refs.tooltipActivator,
        this.containerRef,
      );

      const elementWidth = this.tooltipSize.width;
      const elementHeight = this.tooltipSize.height + TOOLTIP_OFFSET_TO_ANCHOR_PIXELS;

      let optimalVertical = 'bottom';
      if (elementHeight > distances.bottom && distances.top > distances.bottom) {
        optimalVertical = 'top';
      }

      let optimalHorizontal = null;

      const elementHalfSizeWithExtraSpace = elementWidth / 2 + 20;

      if (distances.left < elementHalfSizeWithExtraSpace) {
        optimalHorizontal = 'right';
      } else if (distances.right < elementHalfSizeWithExtraSpace) {
        optimalHorizontal = 'left';
      }

      return { optimalVertical, optimalHorizontal };
    },
  },
};
</script>
